import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";

function AssignSchools({
  assignments,
  qaUsers,
  centers,
  provinces,
  cities,
  stats,
  onFetchCities,
  onFetchCenters,
  onAddAssignment,
  onDeleteAssignment,
}) {
  const [filterUser, setFilterUser] = useState("ALL");
  const { register, handleSubmit, reset, setValue, watch } = useForm();

  const watchProvince = watch("province");
  const watchCity = watch("city");

  // Fetch cities when province changes
  useEffect(() => {
    if (watchProvince) {
      onFetchCities(watchProvince);
    }
    // Always clear dependent fields when province changes
    setValue("city", "");
    setValue("center", "");
  }, [watchProvince, onFetchCities, setValue]);

  // Fetch centers when city changes
  useEffect(() => {
    if (watchCity) {
      onFetchCenters(watchCity);
    }
    // Always clear center when city changes
    setValue("center", "");
  }, [watchCity, onFetchCenters, setValue]);

  // Filter assignments by selected QA user
  const filteredAssignments = filterUser === "ALL" 
    ? assignments 
    : assignments.filter(a => a.qaUserId === filterUser);

  const handleFormSubmit = async (data) => {
    const assignmentData = {
      qaUserId: data.qaUser,
      centerId: data.center,
      notes: data.notes,
      assignedBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
    };

    const success = await onAddAssignment(assignmentData);
    if (success) {
      reset();
      setValue("province", "");
      setValue("city", "");
    }
  };

  const getCenterName = useCallback((centerId) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.name : centerId || "Unknown Center";
  }, [centers]);

  const getCityName = useCallback((centerId) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.city : "Unknown City";
  }, [centers]);

  const getUserName = useCallback((userId) => {
    const user = qaUsers.find(u => String(u.id) === String(userId));
    return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
  }, [qaUsers]);

  return (
    <div className="row g-3">
      {/* Left Column - Form & Stats */}
      <div className="col-lg-4">
        {/* Assignment Form */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Assign Center to QA User
          </div>
          
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="mb-2">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                QA User <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                {...register("qaUser", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#212529',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #ced4da',
                  borderRadius: '0.25rem',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                required
              >
                <option value="">Select QA User</option>
                {qaUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
              <div className="small-helper mt-1" style={{ fontSize: '.75rem', color: '#6c757d' }}>
                Only QA role users are selectable
              </div>
            </div>

            <div className="mb-2">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                Province/State <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                {...register("province", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#212529',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #ced4da',
                  borderRadius: '0.25rem'
                }}
                required
              >
                <option value="">Select Province</option>
                {provinces.map(p => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                City <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                {...register("city", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#212529',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #ced4da',
                  borderRadius: '0.25rem'
                }}
                disabled={!watchProvince}
                required
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.code} value={city.code}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                Center <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                {...register("center", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#212529',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #ced4da',
                  borderRadius: '0.25rem'
                }}
                required
                disabled={!watchCity}
              >
                <option value="">Select Center</option>
                {centers.map(center => (
                  <option key={center.id} value={center.id}>
                    {center.name} ({center.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-2">
              <label className="form-label" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                Assignment Notes
              </label>
              <textarea
                {...register("notes")}
                rows="2"
                className="form-control form-control-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#212529',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #ced4da',
                  borderRadius: '0.25rem'
                }}
                placeholder="Optional notes (e.g., coverage area, special focus)"
              />
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => reset()}
                className="btn btn-outline-secondary btn-sm"
              >
                <i className="fas fa-times-circle mr-2"></i>
                Clear
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
              >
                <i className="fas fa-plus-circle mr-2"></i>
                Assign
              </button>
            </div>
          </form>
        </div>

        {/* Stats */}
        <div className="form-section" style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          background: 'white',
          marginBottom: '1rem'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '.95rem',
            marginBottom: '.75rem'
          }}>
            Quick KPIs
          </div>
          
          <div className="row g-2">
            <div className="col-6">
              <div className="kpi" style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: '#fff',
                padding: '.85rem 1rem',
                height: '100%'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.25rem' 
                }}>
                  {stats.totalAssignments}
                </div>
                <div className="label" style={{ 
                  color: '#6c757d', 
                  fontSize: '.8rem' 
                }}>
                  Total Assignments
                </div>
              </div>
            </div>
            
            <div className="col-6">
              <div className="kpi" style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: '#fff',
                padding: '.85rem 1rem',
                height: '100%'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.25rem' 
                }}>
                  {stats.totalQaUsers}
                </div>
                <div className="label" style={{ 
                  color: '#6c757d', 
                  fontSize: '.8rem' 
                }}>
                  QA Users
                </div>
              </div>
            </div>
            
            <div className="col-12">
              <div className="kpi" style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                background: '#fff',
                padding: '.85rem 1rem',
                height: '100%'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.25rem' 
                }}>
                  {stats.totalCenters}
                </div>
                <div className="label" style={{ 
                  color: '#6c757d', 
                  fontSize: '.8rem' 
                }}>
                  Centers (Master)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Assignments Table */}
      <div className="col-lg-8">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600 }}>
            Assigned Centers
          </h6>
          
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: '.875rem' }}>Filter:</span>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: '220px' }}
            >
              <option value="ALL">All QA Users</option>
              {qaUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th style={{ width: '42px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>#</th>
                <th style={{ width: '150px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>QA User</th>
                <th style={{ fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Center</th>
                <th style={{ width: '170px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Assigned On</th>
                <th style={{ width: '180px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Notes</th>
                <th style={{ width: '80px', fontSize: '.75rem', textTransform: 'uppercase', color: '#6c757d' }}>Actions</th>
              </tr>
            </thead>
            
            <tbody>
              {filteredAssignments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No assignments found
                  </td>
                </tr>
              ) : (
                filteredAssignments.map((assignment, index) => (
                  <tr key={assignment.id}>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {index + 1}
                    </td>
                    <td style={{ fontSize: '.875rem', fontWeight: 500 }}>
                      {getUserName(assignment.qaUserId)}
                    </td>
                    <td style={{ fontSize: '.875rem' }}>
                      {getCenterName(assignment.centerId)}
                    </td>
                    <td style={{ 
                      fontSize: '.875rem', 
                      color: '#6c757d',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                    }}>
                      {assignment.createdAt}
                    </td>
                    <td style={{ fontSize: '.875rem', color: '#6c757d' }}>
                      {assignment.notes || (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={async () => {
                          if (window.confirm("Are you sure you want to remove this assignment?")) {
                            await onDeleteAssignment(assignment.id);
                          }
                        }}
                        className="btn btn-outline-danger btn-sm"
                        title="Remove Assignment"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="small-helper mt-2" style={{ fontSize: '.75rem', color: '#6c757d' }}>
          <i className="fas fa-info-circle mr-1"></i>
          Tip: Scheduling uses these assignments. Only assigned centers can be scheduled for a QA user.
        </div>
      </div>
    </div>
  );
}

export default AssignSchools;